import { Menu, LogOut } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/contexts/I18nContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import logo from '@/assets/images/logo/logo.svg';
import { LanguageSelector } from '@/components/ui/LanguageSelector';
import { NotificationBell } from '@/components/ui/NotificationBell';
import { NotificationsScreen } from '@/components/notifications/NotificationsScreen';
import { useNotifications } from '@/hooks/useNotifications';

export const TopNavigation = ({ bgColor, variant }: { bgColor?: string, variant?: string }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const notificationsHook = useNotifications();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const navClass = 'fixed w-full top-0 z-50';

  return (
    <nav
      className={navClass}
      style={{ background: 'rgba(40,27,27,0.5)', backdropFilter: 'blur(8px)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Language Selector */}
          <div className="flex items-center">
            <LanguageSelector />
          </div>

          {/* Logo */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <img 
                src={logo} 
                alt="sOSSiety Logo" 
                className="h-8 w-auto"
              />
            </div>
          </div>

          {/* Right Side - Notifications and Hamburger Menu */}
          <div className="flex items-center space-x-1">
            {/* Notification Bell (only if logged in) */}
            {user && (
              <NotificationBell 
                unreadCount={notificationsHook.unreadCount}
                openNotifications={notificationsHook.openNotifications}
                isOpen={notificationsHook.isOpen}
              />
            )}

            {/* Hamburger Menu */}
            <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button className={`p-1 ${isMenuOpen ? 'text-white' : 'text-[#8D8D8D]'} hover:text-white`}>
                  <Menu className={`w-6 h-6 ${isMenuOpen ? 'fill-white' : ''}`} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#2C2E32] border-[#5C5C5C]">
                <DropdownMenuItem className="text-[#E1E1E1] cursor-default opacity-100" onClick={() => setIsMenuOpen(false)} disabled>
                  About sOSSiety
                </DropdownMenuItem>
                <DropdownMenuItem className="text-[#E1E1E1] hover:bg-[#5C5C5C] hover:text-[#E1E1E1]" onClick={() => { navigate('/terms-of-use'); setIsMenuOpen(false); }}>
                  Terms of Use
                </DropdownMenuItem>
                <DropdownMenuItem className="text-[#E1E1E1] hover:bg-[#5C5C5C] hover:text-[#E1E1E1]" onClick={() => { navigate('/privacy-policies'); setIsMenuOpen(false); }}>
                  Privacy Policies
                </DropdownMenuItem>
                <DropdownMenuItem className="text-[#E1E1E1] hover:bg-[#5C5C5C] hover:text-[#E1E1E1]" onClick={() => { navigate('/invite-friend'); setIsMenuOpen(false); }}>
                  Invite a Friend
                </DropdownMenuItem>
                <DropdownMenuItem className="text-[#E1E1E1] hover:bg-[#5C5C5C] hover:text-[#E1E1E1]" onClick={() => { navigate('/contact'); setIsMenuOpen(false); }}>
                  Contact us
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#5C5C5C]" />
                <DropdownMenuItem onClick={() => { handleSignOut(); setIsMenuOpen(false); }} className="text-[#E1E1E1] hover:bg-[#5C5C5C] hover:text-[#E1E1E1]">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t('auth.sign_out')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* Notifications Screen */}
      <NotificationsScreen 
        isOpen={notificationsHook.isOpen}
        onClose={notificationsHook.closeNotifications}
        notifications={notificationsHook.notifications}
        isLoading={notificationsHook.isLoading}
        onNotificationClick={notificationsHook.handleNotificationClick}
        onMarkAllAsRead={notificationsHook.markAllAsRead}
        isMarkingAllAsRead={notificationsHook.isMarkingAllAsRead}
        unreadCount={notificationsHook.unreadCount}
      />

    </nav>
  );
};
